import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, GenericModal, Input } from '@org/ui';
import { trpc } from '@org/clients/school';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getMutationErrorMessage } from '../../app/components/shared/multi-step-utils';
import { FormField, MediaField } from '../../app/components/woman/story-form-fields';
import {
  isHttpUrl,
  stashRequestErrors,
  type WomanRequestErrors,
} from '../../app/components/woman/woman-editor-helpers';

export const Route = createFileRoute('/women/new')({
  component: WomanCreateRoute,
});

function WomanCreateRoute() {
  const navigate = useNavigate({ from: '/women/new' });
  const utils = trpc.useUtils();
  const { t } = useTranslation('admin');

  const [name, setName] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const createMutation = trpc.women.create.useMutation();
  const portraitMutation = trpc.women.requestPortrait.useMutation();
  const draftMutation = trpc.women.requestDraft.useMutation();

  const trimmedName = name.trim();
  const trimmedPhoto = photoUrl.trim();
  const photoInvalid = trimmedPhoto.length > 0 && !isHttpUrl(trimmedPhoto);

  const handleClose = () => {
    navigate({ to: '/women', replace: true });
  };

  const handleCreate = async () => {
    if (!trimmedName || photoInvalid || submitting) return;
    setSubmitting(true);
    try {
      const created = await createMutation.mutateAsync({
        name: trimmedName,
        ...(trimmedPhoto ? { referencePhotoUrl: trimmedPhoto } : {}),
      });
      const womanId = created.womanId;

      // Ask the AI for her portrait and her story right away. If a request
      // cannot be queued she still exists: go to her page and show why there.
      const errors: WomanRequestErrors = {};
      if (trimmedPhoto) {
        try {
          await portraitMutation.mutateAsync({ womanId, referencePhotoUrl: trimmedPhoto });
        } catch (error) {
          errors.portrait = getMutationErrorMessage(error);
        }
      }
      try {
        await draftMutation.mutateAsync({ womanId });
      } catch (error) {
        errors.draft = getMutationErrorMessage(error);
      }
      stashRequestErrors(womanId, errors);

      await utils.women.list.invalidate();
      toast.success(t('women.new.toasts.created'));
      navigate({ to: '/women/$womanId', params: { womanId }, replace: true });
    } catch (error) {
      toast.error(t('women.new.toasts.createError', { message: getMutationErrorMessage(error) }));
      setSubmitting(false);
    }
  };

  return (
    <GenericModal
      show
      onClose={handleClose}
      title={t('women.new.title')}
      size="lg"
      centered
      body={
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">{t('women.new.intro')}</p>
          <FormField htmlFor="new-woman-name" label={t('women.new.name')}>
            <Input
              id="new-woman-name"
              autoFocus
              value={name}
              placeholder={t('women.new.namePlaceholder')}
              onChange={(event) => setName(event.target.value)}
            />
          </FormField>
          <FormField label={t('women.new.photo')} hint={t('women.new.photoHint')}>
            <MediaField idBase="new-woman-photo" kind="image" url={photoUrl} onUrlChange={setPhotoUrl} />
          </FormField>
          {photoInvalid ? <p className="text-xs text-red-600">{t('women.editor.portrait.referenceInvalid')}</p> : null}
          <p className="text-xs text-muted-foreground">{t('women.editor.jobs.takesMinutes')}</p>
        </div>
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="button" onClick={handleCreate} disabled={!trimmedName || photoInvalid || submitting}>
            {submitting ? t('common.status.creating') : t('women.new.create')}
          </Button>
        </>
      }
    />
  );
}
